import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { format, formatDistanceToNowStrict } from "date-fns";
import { ComponentType, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { CellProps } from "react-table";

import { SlideAdmin, AdminSlideList, AdminCaseList, Stain } from "pwapi";

import { SlideStatusChange } from "custom-models";

import { setAdminLastSlideViewed } from "shared/actions/admin/adminSlidesActions";
import { createCompareAdminSlides, slideLabel } from "shared/utils/slideUtils";
import { getStainByLabStainId } from "shared/utils/stainUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { UnderlineToggleButton } from "components/customInputs/buttons/ToggleButton";
import { useAppSelector } from "store/hooks";

import { SlidesTableProps } from "./SlidesTable";
import { extractBasePathname, getTimeDifference } from "../adminUtils";
import ReactTableExpandableCell from "../table/ReactTableExpandableCell";

// TODO: extract a "CasesAdmin" (note plural) slide from OpenAPI
export const getSlideStatusChange = (
  slide: AdminSlideList | SlideAdmin | AdminCaseList["slides"][0]
) => {
  // TODO: Extracted from within the cell, but could be cleaner
  let statusChanges: SlideStatusChange[] =
    (slide as SlideAdmin).status_changes || [];
  if (statusChanges.length) {
    // Get the latest status change date
    // Or the only date in the list
    const statusChange: SlideStatusChange =
      statusChanges.length > 1
        ? statusChanges.reduce((dateA, dateB) =>
            new Date(dateA.created_at || "") > new Date(dateB.created_at || "")
              ? dateA
              : dateB
          )
        : statusChanges[0];
    return statusChange;
  }
};

const SlidesColumns =
  (
    Component: ComponentType<SlidesTableProps>,
    options?: {
      displayCaseInfo?: boolean;
      hideSlideRetention?: boolean;
      setLastViewed?: boolean;
    }
  ) =>
  (props: SlidesTableProps) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const stains = useAppSelector(({ stains }) => stains);
    const labStains = useAppSelector(({ labStains }) => labStains);

    const setLastViewed = (slide_id: number) => {
      options?.setLastViewed && dispatch(setAdminLastSlideViewed(slide_id));
    };

    const compareSlides = createCompareAdminSlides(stains);

    const sortType = useCallback(
      (labelA: CellProps<AdminSlideList>, labelB: CellProps<AdminSlideList>) =>
        compareSlides(labelA.original, labelB.original),
      []
    );

    const columns = useMemo(
      () => [
        {
          id: "barcode",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            return (
              <div
                className={classNames("cell__icon", "slideIcon--barcode", {
                  "cell__icon--expanded": row.isExpanded,
                })}
                onClick={() => {
                  row.toggleRowExpanded();
                }}
              >
                <FontAwesomeIcon icon="qrcode" />
              </div>
            );
          },
        },
        {
          accessor: "flag",
          Cell: ({ row }: CellProps<AdminSlideList>) =>
            row.original.flags?.length > 0 && (
              <div className="hoverInfo__container cell__icon">
                <FontAwesomeIcon icon="flag" color="var(--imperialRed)" />
                <div className="hoverInfo__display hoverInfo__display--warning">
                  <p>Slide Flags</p>
                  <span>
                    {row.original.flags
                      .filter((flag) => flag.reason)
                      .map((flag) => (
                        <div className="hoverInfo__flag" key={flag.id}>
                          <span className="hoverInfo__flagReason">
                            <strong>{flag.reason.replace("-", " ")}</strong>
                            <br />
                            <small>
                              {flag.order_id && (
                                <>
                                  Order:{" "}
                                  {
                                    getStainByLabStainId(
                                      row.original.lab_orders?.find(
                                        (lo) => lo.id === flag.order_id
                                      )?.lab_stain_id || -1,
                                      stains,
                                      labStains
                                    )?.name
                                  }
                                  <br />
                                </>
                              )}
                              {flag.note && (
                                <>
                                  "{flag.note}"<br />
                                </>
                              )}
                              {flag.user &&
                                `${flag.user?.first_name} ${flag.user?.last_name}`}{" "}
                              &mdash;{" "}
                              {flag.created_at &&
                                `${formatDistanceToNowStrict(
                                  new Date(flag.created_at),
                                  { addSuffix: true }
                                )}`}
                            </small>
                          </span>
                        </div>
                      ))}
                  </span>
                </div>
              </div>
            ),
        },
        ...(options?.displayCaseInfo
          ? [
              {
                Header: "Case Accession",
                accessor: "accession",
                Cell: ({ row }: { row: { original: AdminSlideList } }) => {
                  return <>{row.original.case?.accession}</>;
                },
              },
            ]
          : []),
        {
          Header: "Slide Accession",
          accessor: "accession_number",
        },
        {
          Header: "Created At",
          accessor: "created_at",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            return row.original.created_at ? (
              <>{format(new Date(row.original.created_at), "MM/dd/yyyy")}</>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "Received Date",
          accessor: "received_date",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            if (row.original.specimen && row.original.specimen.received_date) {
              return getTimeDifference(
                new Date(row.original.specimen.received_date)
              );
            }
            return <></>;
          },
        },
        {
          Header: "Uploaded Date",
          accessor: "uploaded_date",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            const statusChanges = row.original.status_changes;
            if (!statusChanges) return <></>;

            const uploadDateIndex = statusChanges.findIndex(
              (status) => status.new_status === "uploaded"
            );
            if (uploadDateIndex >= 0) {
              const uploadDate = new Date(
                statusChanges[uploadDateIndex].created_at || ""
              );
              return <>{format(uploadDate, "MM/dd/yyyy")}</>;
            }
            return <></>;
          },
        },
        {
          Header: "SID",
          accessor: "label",
          sortType,
          Cell: ({ row }: CellProps<AdminSlideList>) =>
            slideLabel(
              row.original,
              row.original.block_obj,
              row.original.specimen,
              stains[row.original.stain_id || 0]?.category ===
                Stain.category.ROUTINE
                ? undefined
                : stains[row.original.stain_id || 0]
            ),
        },
        {
          Header: "Stain",
          accessor: "stain",
          Cell: ({ row }: CellProps<AdminSlideList>) => (
            <span style={{ whiteSpace: "nowrap" }}>
              {stains[row.original.stain_id || 0]?.name}
              {row.original.is_control && " CONTROL"}
              {row.original.is_rescan && " (Rescan)"}
            </span>
          ),
        },

        {
          Header: "Specimen Biopsy",
          accessor: "specimen_biopsy",
          Cell: ({ row }: CellProps<AdminSlideList>) => (
            <>{row.original.specimen?.biopsy_type}</>
          ),
        },

        {
          Header: "QC Status",
          accessor: "qc_status",
          Cell: ({ row, value }: CellProps<AdminSlideList>) => {
            return row.original.qc_notes ? (
              <div className="cell__content--horizontal">
                {value}
                <div className="hoverInfo__container cell_icon">
                  <FontAwesomeIcon
                    icon="sticky-note"
                    className="slideIcon--note"
                  />
                  <div className="hoverInfo__display hoverInfo__display--note">
                    <p>QC Notes</p>
                    <span>{row.original.qc_notes || "No notes"}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>{value}</>
            );
          },
        },

        {
          Header: "Status",
          accessor: "status",
          Cell: ({ row, value }: CellProps<AdminSlideList>) => {
            return value === "manual-review" ? (
              <ReactTableExpandableCell row={row} expandedColor="var(--blue)">
                <UnderlineToggleButton
                  title="manual review slide"
                  isToggled={row.isExpanded}
                  onClick={() => {
                    row.toggleRowExpanded();
                  }}
                >
                  Manual Review
                </UnderlineToggleButton>
              </ReactTableExpandableCell>
            ) : (
              value
            );
          },
        },

        {
          Header: "Status Age",
          accessor: "status_age",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            const statusChange = getSlideStatusChange(row.original);
            if (statusChange && statusChange.created_at) {
              return getTimeDifference(new Date(statusChange.created_at));
            }
            return <></>;
          },
        },

        {
          Header: "Actions",
          Cell: ({ row }: CellProps<AdminSlideList>) => {
            const pathname = extractBasePathname(location.pathname);
            const slideEditUrl = location.pathname.includes("/slides")
              ? `${pathname}${row.original.id}`
              : pathname === "/admin/"
              ? `${pathname}cases/slides/${row.original.id}`
              : `${pathname}slides/${row.original.id}`;
            return (
              <div
                className="cell__content--horizontal"
                style={{ gap: "0.5em" }}
              >
                <Link
                  to={{
                    pathname: slideEditUrl,
                    search: location.search,
                  }}
                  onClick={() => {
                    setLastViewed(row.original.id || 0);
                  }}
                >
                  <PathologyWatchButton size="small">Edit</PathologyWatchButton>
                </Link>
                {options?.displayCaseInfo && row.original.case?.id && (
                  <Link
                    to={`/admin/cases/${row.original.case?.id}/`}
                    target="_blank"
                  >
                    <PathologyWatchButton size="small">
                      Go to Case&nbsp;&rarr;
                    </PathologyWatchButton>
                  </Link>
                )}
                <Link
                  to={
                    row.original.case?.id
                      ? `/cases/${row.original.case?.id}/slides/${row.original.id}`
                      : `/slides/${row.original.id}`
                  }
                  onClick={() => {
                    setLastViewed(row.original.id || 0);
                  }}
                  target="_blank"
                >
                  <PathologyWatchButton size="small">
                    View&nbsp;&rarr;
                  </PathologyWatchButton>
                </Link>
              </div>
            );
          },
        },
      ],
      [location]
    );

    return <Component columns={columns} {...props} />;
  };

export default SlidesColumns;
